import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  TextField,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import "react-phone-input-2/lib/bootstrap.css";
import { showError, showToast } from "../../constants/toasts";
import {
  useAddRemainderMutation,
  useLazyGetRemainderByIdQuery,
  useUpdateRemainderMutation,
} from "../../services/reminder";
import IfCan from "../../components/IfCan";

const FEATURE_OPTIONS = [
  { value: "SMART_HYDRATION_NUDGES", label: "Smart Hydration Nudges" },
  {
    value: "CALENDAR_SYNC_WINDOW_BEHAVIOR",
    label: "Calendar Sync Window Behavior",
  },
  {
    value: "WEATHER_BASED_HYDRATION_ALGORITHM",
    label: "Weather-Based Hydration Algorithm",
  },
];

const WEATHER_OPTIONS = [
  { value: "SUMMER", label: "Summer" },
  { value: "WINTER", label: "Winter" },
  { value: "SPRING", label: "Spring" },
  { value: "AUTUMN", label: "Autumn" },
];

const MAX_LEN = 200;

const AddReminder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editIdParam = searchParams.get("id");
  const isEdit = useMemo(() => Boolean(editIdParam), [editIdParam]);

  const [featureName, setFeatureName] = useState<string>("");
  const [weather, setWeather] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const [addReminder] = useAddRemainderMutation();
  const [updateReminder] = useUpdateRemainderMutation();
  const [getReminderById] = useLazyGetRemainderByIdQuery();

  useEffect(() => {
    const init = async () => {
      if (isEdit && editIdParam) {
        try {
          const idNum = parseInt(editIdParam, 10);
          if (!idNum || Number.isNaN(idNum)) return;
          const res = await getReminderById({ id: idNum }).unwrap();
          const data = res?.data || {};
          const rawFeature = (
            data?.Feature_name ||
            data?.feature_name ||
            data?.featureName ||
            ""
          ).toString();
          const rawWeather = (data?.weather || "").toString();

          const featureFromValue = FEATURE_OPTIONS.find(
            (o) => o.value === rawFeature
          )?.value;
          const featureFromLabel = FEATURE_OPTIONS.find(
            (o) => o.label.toLowerCase() === rawFeature.toLowerCase()
          )?.value;
          setFeatureName(featureFromValue || featureFromLabel || rawFeature);

          const weatherFromValue = WEATHER_OPTIONS.find(
            (o) => o.value === rawWeather
          )?.value;
          const weatherFromLabel = WEATHER_OPTIONS.find(
            (o) => o.label.toLowerCase() === rawWeather.toLowerCase()
          )?.value;
          setWeather(weatherFromValue || weatherFromLabel || rawWeather);

          setMessage(
            (data?.message || data?.description || "")
              .toString()
              .slice(0, MAX_LEN)
          );
        } catch (e: any) {
          showError(e?.data?.message || "Failed to load details");
        }
      } else {
        setFeatureName("");
        setWeather("");
        setMessage("");
      }
    };
    init();
  }, [isEdit, editIdParam, getReminderById]);

  const handleSubmit = async () => {
    setError(true);
    const trimmedFeatureName = (featureName || "").trim();
    const trimmedWeather = (weather || "").trim();
    const trimmedMessage = (message || "").trim();

    if (!trimmedFeatureName) {
      showError("Feature name is required");
      return;
    }
    if (!trimmedWeather) {
      showError("Weather is required");
      return;
    }
    if (!trimmedMessage) {
      showError("Message is required");
      return;
    }
    if (trimmedMessage.length > MAX_LEN) {
      showError(`Max ${MAX_LEN} characters allowed`);
      return;
    }

    const body: any = {
      Feature_name: trimmedFeatureName,
      weather: trimmedWeather,
      message: trimmedMessage,
      ...(isEdit && editIdParam ? { id: Number(editIdParam) } : {}),
    };
    setIsLoading(true);
    try {
      let res: any;
      if (isEdit && editIdParam) {
        res = await updateReminder({ body, id: Number(editIdParam) }).unwrap();
      } else {
        res = await addReminder({ body }).unwrap();
      }
      if (res?.status === 200 || res?.status === 201) {
        showToast(
          isEdit
            ? "Reminder updated successfully"
            : "Reminder added successfully"
        );
        navigate("/reminder-engine");
      }
    } catch (e: any) {
      showError(e?.data?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <IfCan module="Reminder Engine" action="view">
        <div className="main_title">
          <h1>{isEdit ? "Edit" : "Add"} Reminder Engine</h1>
          <p>
            <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
            <span onClick={() => navigate("/reminder-engine")}>
              Reminder Engine
            </span>{" "}
            - {isEdit ? "Edit" : "Add"} Reminder Engine
          </p>
        </div>

        <div className="cards">
          <div className="form">
          <div className="gap_p">
            <div className="control_group w_33">
              <label>Feature Name</label>
              <Select
                fullWidth
                value={featureName || ""}
                onChange={(e: SelectChangeEvent<string>) =>
                  setFeatureName(e.target.value as string)
                }
                displayEmpty
                renderValue={(selected) => {
                  if (!selected || !String(selected).length) return "Select";
                  const opt = FEATURE_OPTIONS.find((o) => o.value === selected);
                  return opt ? opt.label : String(selected);
                }}
              >
                <MenuItem disabled value="">
                  Select{" "}
                </MenuItem>
                {FEATURE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {error && !featureName ? (
                <h6 className="err_msg">Feature name is required</h6>
              ) : (
                ""
              )}
            </div>

            <div className="control_group w_33">
              <label>Select Weather</label>
              <Select
                fullWidth
                value={weather || ""}
                onChange={(e: SelectChangeEvent<string>) =>
                  setWeather(e.target.value as string)
                }
                displayEmpty
                renderValue={(selected) => {
                  if (!selected || !String(selected).length) return "Select";
                  const opt = WEATHER_OPTIONS.find((o) => o.value === selected);
                  return opt ? opt.label : String(selected);
                }}
              >
                <MenuItem disabled value="">
                  Select{" "}
                </MenuItem>
                {WEATHER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {error && !weather ? (
                <h6 className="err_msg">Weather is required</h6>
              ) : (
                ""
              )}
            </div>

            <div className="control_group w_100">
              <label>Message</label>
              <TextField
                placeholder="Enter"
                hiddenLabel
                fullWidth
                multiline
                rows={2}
                value={message}
                inputProps={{ maxLength: MAX_LEN }}
                onChange={(e) => setMessage(e.target.value)}
              />
              {error && !message.trim() ? (
                <h6 className="err_msg">Message is required</h6>
              ) : (
                ""
              )}
            </div>
          </div>
            <div className="form_btn">
              <IfCan module="Reminder Engine" action="add_edit">
                <Button
                  className="btn btn_primary"
                  disabled={isLoading}
                  onClick={handleSubmit}
                >
                  {isEdit
                    ? isLoading
                      ? "Updating..."
                      : "Update"
                    : isLoading
                    ? "Saving..."
                    : "Save"}
                </Button>
              </IfCan>
            </div>
          </div>
        </div>
      </IfCan>
    </>
  );
};

export default AddReminder;
